import { Controller } from "@hotwired/stimulus"

/**
 * Inserts a cropping step between selecting a file in a hidden `<input type="file">`
 * and submitting the surrounding form.
 *
 * Usage:
 *   <div data-controller="image-input-cropper">
 *     <%= render 'shared/image_cropper_modal' %>
 *     <%= form.file_field :photo,
 *           data: {
 *             image_input_cropper_target: 'input',
 *             action: 'change->image-input-cropper#fileSelected'
 *           } %>
 *   </div>
 *
 * The cropper modal partial renders the `image-cropper` controller, which listens
 * for the `image-cropper:request-crop` event this controller dispatches.
 */
export default class extends Controller {
  static targets = ["input"]
  static values = {
    autoSubmit: { type: Boolean, default: true }
  }

  declare readonly inputTarget: HTMLInputElement
  declare readonly autoSubmitValue: boolean

  fileSelected(event: Event) {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    if (!file) return

    if (!this.shouldCrop(file)) {
      this.submitForm()
      return
    }

    document.dispatchEvent(
      new CustomEvent("image-cropper:request-crop", {
        detail: {
          file,
          onCropped: (croppedFile: File) => {
            this.replaceInputFile(croppedFile)
            this.submitForm()
          },
          onCancel: () => {
            this.clearInput()
          }
        }
      })
    )
  }

  private shouldCrop(file: File): boolean {
    if (!file.type) return false
    if (!file.type.startsWith("image/")) return false
    if (file.type.includes("svg")) return false
    return true
  }

  private replaceInputFile(file: File) {
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    this.inputTarget.files = dataTransfer.files
  }

  private clearInput() {
    this.inputTarget.value = ""
  }

  private submitForm() {
    if (!this.autoSubmitValue) return

    const form = this.inputTarget.form
    if (form) form.requestSubmit()
  }
}
